import { css } from '@emotion/core'
import styled from '@emotion/styled'
import Cleave from 'cleave.js/react'

import { colors, palette, spacing, typography } from '../constants'
import { Text } from '../typography'

const borderRadius = '3px'
const paddingX = `${spacing.majorScale(2)}px`

interface IFieldContainerProps {
  disabled: boolean
  focused: boolean
  invalid: boolean
  roundedCorners?: boolean
}

function pickBorderRadius(roundedCorners = false): string {
  if (roundedCorners) return `${spacing.majorScale(1)}px`

  return borderRadius
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const FieldContainer = styled(Text.withComponent('div'))<IFieldContainerProps>`
  border: 1px solid;
  border-radius: ${({ roundedCorners }) => pickBorderRadius(roundedCorners)};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 56px;
  width: 100%;
  justify-content: flex-end;
  position: relative;

  border-color: ${({ disabled, focused, invalid }) => {
    if (disabled) return colors.border.disabled
    if (invalid) return colors.border.error
    if (focused) return colors.border.active

    return colors.border.default
  }};
`

interface ILabelProps {
  active: boolean
  focused: boolean
  invalid: boolean
}

export const Label = styled.label<ILabelProps>`
  cursor: text;
  font-size: ${typography.fontSize.label.textField};
  left: 0;
  line-height: 12px;
  margin: 0;
  padding: 0 ${paddingX};
  pointer-events: none;
  position: absolute;
  top: ${spacing.majorScale(1)}px;

  color: ${({ focused, invalid }) => {
    if (invalid) return colors.text.error
    if (focused) return colors.text.active

    return colors.text.hint
  }};

  display: ${({ active, focused, invalid }) => {
    return active || invalid || focused ? 'inherit' : 'none'
  }};

  font-weight: ${({ focused, invalid }) => {
    if (invalid) return typography.fontWeight.bold
    if (focused) return typography.fontWeight.bold

    return typography.fontWeight.regular
  }};

  // HACK (alex): cross-bundle compatibility
  text-transform: none;
`

interface IInputProps {
  hasLabel: boolean
  roundedCorners?: boolean
}

function inputStyles(props: IInputProps) {
  const { hasLabel, roundedCorners } = props

  return css`
    background-color: ${palette.white};
    border: none;
    border-radius: ${pickBorderRadius(roundedCorners)};
    height: 100%;
    margin: 0;
    padding: 0 ${paddingX};
    padding-top: ${hasLabel ? '20px' : '0'};
    font-size: ${typography.fontSize.text};

    &:focus {
      border: none;
      outline: none;
    }

    &::placeholder {
      color: ${colors.text.hint};
    }

    &:disabled {
      color: ${colors.text.disabled};
    }

    &:disabled::placeholder {
      color: ${colors.text.disabled};
    }
  `
}

export const Input = styled('input', {
  shouldForwardProp: propName => !['hasLabel', 'roundedCorners'].includes(propName),
})<IInputProps>`
  ${inputStyles}

  // HACK (alex): cross-bundle compatibility
  &[type='password'] {
    ${inputStyles}
  }
`

export const CleaveInput = Input.withComponent(Cleave)

export const ValidationHint = styled(Text)`
  color: ${colors.text.error};
  margin-top: ${spacing.majorScale(1)}px;
`
